import React from 'react';
import LegalLayout, { LegalSection } from 'components/ui/LegalLayout';
import Icon from 'components/AppIcon';
import withPageOverride from './withPageOverride';

const SUPPORT_EMAIL = 'support@secureauth.local';

const SupportDefault = () => (
    <LegalLayout title="Поддержка" lastUpdated="19 мая 2026 г.">
        <p>
            Мы стараемся, чтобы работа с <strong>SecureAuth Enterprise</strong> была
            максимально простой и предсказуемой. Если у вас возникли вопросы или
            проблемы — свяжитесь с командой поддержки одним из удобных способов ниже.
        </p>

        <LegalSection title="Связаться с нами">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="
                        flex items-center gap-3 p-4 border border-secondary-200 rounded-md
                        hover:border-primary hover:bg-primary-50 transition-colors duration-150
                    "
                >
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                        <Icon name="Mail" size={20} className="text-primary" />
                    </div>
                    <div>
                        <p className="font-medium text-text-primary">Email поддержки</p>
                        <p className="text-sm text-text-secondary">{SUPPORT_EMAIL}</p>
                    </div>
                </a>

                <div className="flex items-center gap-3 p-4 border border-secondary-200 rounded-md">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                        <Icon name="Clock" size={20} className="text-primary" />
                    </div>
                    <div>
                        <p className="font-medium text-text-primary">Режим работы</p>
                        <p className="text-sm text-text-secondary">Пн–Пт, 09:00 – 19:00 (МСК)</p>
                    </div>
                </div>
            </div>
            <p className="text-sm text-text-secondary mt-3">
                Среднее время ответа: <strong>до 1 рабочего дня</strong>. Срочные обращения,
                связанные с безопасностью аккаунта, обрабатываются в первую очередь.
            </p>
        </LegalSection>

        <LegalSection title="Перед обращением — проверьте FAQ">
            <FAQItem
                q="Я забыл пароль. Что делать?"
                a={
                    <>
                        Воспользуйтесь страницей{' '}
                        <a href="/password-reset" className="text-primary underline">
                            Восстановление пароля
                        </a>
                        . Ссылка для сброса будет отправлена на email, указанный при
                        регистрации.
                    </>
                }
            />
            <FAQItem
                q="Не приходит письмо подтверждения email"
                a="Проверьте папку «Спам». Если письмо не пришло в течение 10 минут — запросите повторную отправку или напишите в поддержку."
            />
            <FAQItem
                q="Как изменить email или имя пользователя?"
                a="Email-адрес и имя пользователя можно изменить в разделе «Профиль» → «Личная информация». Для смены email потребуется подтверждение по новому адресу."
            />
            <FAQItem
                q="Как завершить активные сессии на других устройствах?"
                a={
                    <>
                        Откройте страницу{' '}
                        <a href="/session" className="text-primary underline">
                            Управление сессиями
                        </a>{' '}
                        и нажмите «Завершить» рядом с нужным устройством либо
                        «Завершить все, кроме текущего».
                    </>
                }
            />
            <FAQItem
                q="Я хочу удалить аккаунт"
                a="Отправьте запрос на удаление через email поддержки. Аккаунт и связанные данные удаляются в течение 90 дней (см. Политику конфиденциальности)."
            />
            <FAQItem
                q="Где сообщить об уязвимости?"
                a={
                    <>
                        Напишите письмо с темой <code>[SECURITY]</code> на адрес{' '}
                        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary underline">
                            {SUPPORT_EMAIL}
                        </a>
                        . Мы благодарны ответственным исследователям и подтверждаем
                        получение в течение 24 часов.
                    </>
                }
            />
        </LegalSection>

        <LegalSection title="Сообщить о проблеме">
            <p>
                При обращении укажите:
            </p>
            <ul className="list-disc pl-6 space-y-1">
                <li>ваш email или имя пользователя;</li>
                <li>краткое описание проблемы и шаги для воспроизведения;</li>
                <li>скриншоты или текст ошибки (если есть);</li>
                <li>браузер и операционную систему.</li>
            </ul>
            <p>
                Чем больше деталей вы предоставите — тем быстрее мы поможем.
            </p>
        </LegalSection>
    </LegalLayout>
);

const FAQItem = ({ q, a }) => (
    <details className="border border-secondary-200 rounded-md p-3 mb-2 group">
        <summary className="cursor-pointer font-medium text-text-primary flex items-center justify-between">
            <span>{q}</span>
            <Icon
                name="ChevronDown"
                size={16}
                className="text-secondary-400 group-open:rotate-180 transition-transform"
            />
        </summary>
        <p className="mt-2 text-sm text-text-secondary">{a}</p>
    </details>
);

export default withPageOverride('support', SupportDefault);
